import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { uiBtn } from '../../ui/buttonClasses';

/** Must match api/_utils/hullStabilityScoring.js roll caps. */
const MAX_PRE_CATCH_ABS_ROLL_MEAN = 0.75;
const MAX_DRIVE_ROLL_STD = 0.5;
const MAX_ABS_ROLL_AT_STROKE = 1.25;

const HullSetInfoPopover: React.FC = () => {
  const [showInfo, setShowInfo] = useState(false);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const infoPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        infoButtonRef.current &&
        !infoButtonRef.current.contains(e.target as Node) &&
        infoPopoverRef.current &&
        !infoPopoverRef.current.contains(e.target as Node)
      ) {
        setShowInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfo]);

  return (
    <div className="relative">
      <button
        ref={infoButtonRef}
        type="button"
        onClick={() => setShowInfo((prev) => !prev)}
        className={uiBtn('icon', { pressed: showInfo })}
        style={{ width: '2rem', height: '2rem' }}
        title="How hull set is calculated"
        aria-label="How hull set is calculated"
      >
        <Info className="h-4 w-4" />
      </button>
      {showInfo && (
        <div
          ref={infoPopoverRef}
          className="absolute right-0 top-full mt-2 z-50 bg-surface border border-border rounded-lg shadow-xl w-[300px]"
        >
          <div className="p-3 space-y-2.5 text-xs text-content-secondary">
            <h4 className="font-semibold text-content-primary">Set vs not set</h4>
            <p>
              Each stroke is scored from Peach boat roll (50&nbsp;Hz) in a window around the catch:
              ~95% of stroke length before catch, ~35% after.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Mean |roll| before catch</li>
              <li>Roll variation during the drive</li>
              <li>|Roll| at the catch</li>
            </ul>
            <p>
              <strong>Set</strong> means all three roll metrics stay under fixed limits (same for
              every session):
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Pre-catch mean |roll| ≤ {MAX_PRE_CATCH_ABS_ROLL_MEAN}°</li>
              <li>Drive roll std dev ≤ {MAX_DRIVE_ROLL_STD}°</li>
              <li>|Roll| at catch ≤ {MAX_ABS_ROLL_AT_STROKE}°</li>
            </ul>
            <p>
              <strong>Not set</strong> means at least one limit was exceeded — the hull was too
              wobbly; gate and power traces may mislead.
            </p>
            <p>On a rocky piece, most strokes may be not set. On a calm piece, most may be set.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HullSetInfoPopover;
